/**
 * Build 5: the shared registers' rows as the platform sends them
 * (routers/registers.py), and its refusals turned into `RowConflict`.
 */
import { z } from "zod";
import { DataSourceError } from "./dataSource";
import { RowConflict, type ConflictKind, type RegisterKey, type RegisterRow } from "../domain/registerRows";

export const wireRow = z.object({
  id: z.string(),
  data: z.record(z.string(), z.unknown()),
  version: z.number().int(),
  created_at: z.string(),
  created_by: z.string().nullish(),
  updated_at: z.string(),
  updated_by: z.string().nullish(),
  deleted_at: z.string().nullish(),
  deleted_by: z.string().nullish(),
  from_note: z.string().nullish(),
});

export const wireRows = z.object({
  register: z.string(),
  rows: z.array(wireRow),
  ever: z.number().int().nullish(),
  can_write: z.boolean().nullish(),
});

export const wireChanged = z.object({ register: z.string(), rows: z.array(wireRow) });
export const wireImported = z.object({ register: z.string(), imported: z.number().int() });

export function fromWireRow(r: z.infer<typeof wireRow>): RegisterRow {
  return {
    id: r.id,
    data: r.data,
    version: r.version,
    createdAt: r.created_at,
    createdBy: r.created_by ?? undefined,
    updatedAt: r.updated_at,
    updatedBy: r.updated_by ?? undefined,
    deletedAt: r.deleted_at ?? undefined,
    deletedBy: r.deleted_by ?? undefined,
    fromNote: r.from_note ?? undefined,
  };
}

const KINDS: readonly ConflictKind[] = ["row_changed", "row_exists", "row_deleted"];

/** A 409 with a row in it is a conflict the screen can show; anything else stays as it was thrown. */
export function asConflict(e: unknown, key: RegisterKey): unknown {
  if (!(e instanceof DataSourceError) || e.status !== 409) return e;
  const d = e.detail as { error?: string; row_id?: string; message?: string; current?: unknown } | undefined;
  if (!d || !KINDS.includes(d.error as ConflictKind)) return e;
  const current = wireRow.safeParse(d.current);
  return new RowConflict(d.message ?? e.message, {
    kind: d.error as ConflictKind,
    register: key,
    rowId: d.row_id ?? "",
    current: current.success ? fromWireRow(current.data) : undefined,
  });
}
